class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('pointerdown', () => { this._suppressClose = true; });
    this.addEventListener('pointerup',   () => { this._suppressClose = false; });
    this.addEventListener('keyup',       () => { this._suppressClose = false; });
    this._outsidePointerDown = false;

    // Détecte si le clic commence en dehors du composant
    this._onDocPointerDown = (e) => { this._outsidePointerDown = !this.contains(e.target); };
    document.addEventListener('pointerdown', this._onDocPointerDown, true); // capture

    // Si tu veux, tu peux reset au pointerup (optionnel mais propre)
    this._onDocPointerUp = () => { setTimeout(() => { this._outsidePointerDown = false; }, 0); };
    document.addEventListener('pointerup', this._onDocPointerUp, true);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(e) {
    // Laisse le navigateur finaliser le focus
    setTimeout(() => {
      // Si on a encore du focus à l'intérieur → ne pas fermer
      if (this.matches(':focus-within')) return;
  
      // Ne fermer QUE si le dernier pointerdown venait de l'extérieur
      if (this._outsidePointerDown) this.close();
  
      // Debug
    }, 0);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('pointerup', this._onDocPointerUp, true);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=20&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
  
    // ⬇️ (Ré)initialise le/les carrousels présents dans les résultats injectés
    this.predictiveSearchResults
      .querySelectorAll('.carousel-container')
      .forEach((el) => this.initCarousel(el));
  
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  initCarousel(container){
    const track = container.querySelector('.carousel-track'),
          prev  = container.querySelector('.carousel-btn.prev'),
          next  = container.querySelector('.carousel-btn.next');
    if(!track || !prev || !next || container._carouselBound) return;
    container._carouselBound = true;
  
    // Focusable (utile quand on "grise" les flèches)
    if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex','-1');
  
    const mq = window.matchMedia('(max-width: 767px)');
    const isMobile = () => mq.matches;
  
    let i=0, w=0, vis=1, max=0, maxPx=0;
  
    const items = () => track.querySelectorAll('.carousel-item');
  
    // On utilise aria-disabled pour éviter les pertes de focus qui déclenchent focusout
    const updBtns = () => {
      const atStart = i<=0, atEnd = i>=max;
      if (atStart && document.activeElement===prev) container.focus();
      if (atEnd   && document.activeElement===next) container.focus();
      prev.setAttribute('aria-disabled', String(atStart));
      next.setAttribute('aria-disabled', String(atEnd));
    };
  
    const apply = () => {
      if (isMobile()){
        // mode natif: aucune translation JS (CSS fait tout)
        track.style.transform = 'none';
        // désactive visuellement les flèches (au cas où le CSS ne les cache pas)
        prev.setAttribute('aria-disabled','true');
        next.setAttribute('aria-disabled','true');
        return;
      }
      const x = Math.min(i*w, maxPx);
      track.style.transform = `translate3d(${-x}px,0,0)`;
      updBtns();
    };
  
    const metrics = () => {
      if (isMobile()){ apply(); return; }
      const it = items();
      if(!it.length){ i=0; max=0; maxPx=0; apply(); return; }
      w = it[0].getBoundingClientRect().width || 0;
      const cw = container.clientWidth || 0;
      const tw = track.scrollWidth || (w*it.length);
      maxPx = Math.max(0, tw - cw);
      vis = Math.max(1, Math.floor(cw / Math.max(1,w)));
      max = Math.max(0, it.length - vis);
      i = Math.min(i, max);
      apply();
    };
  
    const go = d => { if (isMobile()) return; i = Math.max(0, Math.min(i + d, max)); apply(); };
  
    // events desktop
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(+1));
  
    // resize + changement de breakpoint
    window.addEventListener('resize', metrics);
    mq.addEventListener?.('change', metrics);
  
    // recalcul quand la liste change (fetch)
    const mo = new MutationObserver(() => { i=0; metrics(); });
    mo.observe(track, { childList: true });
  
    // recalcul quand le container change
    const ro = new ResizeObserver(metrics);
    ro.observe(container);
  
    // ajuste aussi à la fin du chargement des images (mobile & desktop)
    const bindImgLoad = () => items()
      .forEach(el => el.querySelectorAll('img')
        .forEach(img => { if (!img.complete) img.addEventListener('load', metrics, { once:true }); }));
    bindImgLoad();
  
    // init
    metrics();
  }

}

customElements.define('predictive-search', PredictiveSearch);
